import React, {useEffect, useRef, useState} from 'react';
import {FormattedMessage} from 'react-intl';

import {maxServiceCards} from 'utils/constants/constants';

import Loader from 'components/loader';
import {ICmService, ICmServicePage} from '../../redux/cm-service/interfaces';
import {DmResourceCard} from '../dm-resource-card';

import './styles.scss';

type Props = {
  cards: ICmService[];
  showLoader?: boolean;
  onLoadMore?: (count: number) => void;
  onClickCard: (srvId: number) => void;
  page?: ICmServicePage;
}

export const DmCardSection = ({
  showLoader = false,
  cards,
  onLoadMore,
  onClickCard,
  page
}: Props): JSX.Element => {
  const [countCards, setCountCards] = useState(onLoadMore ? maxServiceCards : cards.length);
  const [totalElements, setTotalElements] = useState(0);
  const [cardsList, setCardsList] = useState<ICmService[]>(cards);
  const footerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (page) {
      setTotalElements(page.totalElements);
    }
  }, [page]);

  useEffect(() => {
    setCardsList(cards);
    setCountCards(cards.length);
  }, [cards]);

  function handleLoadMore() {
    if (onLoadMore) {
      onLoadMore(countCards / maxServiceCards);
    }
    setCountCards(countCards + maxServiceCards);
  }

  function handleClickCard(id: number) {
    return () => {
      onClickCard(id);
    };
  }

  const showing = Math.min(countCards, cardsList.length);
  const hasMore = Boolean(totalElements > 0 && totalElements > maxServiceCards && totalElements > countCards && onLoadMore);

  useEffect(() => {
    const footer = footerRef.current;

    if (!footer || !hasMore || showLoader || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        handleLoadMore();
      }
    }, {rootMargin: '200px'});

    observer.observe(footer);

    return () => observer.disconnect();
  }, [hasMore, showLoader, countCards]);

  return (
    <section className="dm-card-section">
      {totalElements > 0 && (
        <div className="dm-card-section__header">
          <span>
            <FormattedMessage
              id="gritx.cmresource.sectionCards.total"
              defaultMessage="{showing} of {total} resources"
              values={{
                showing: <b>{showing}</b>,
                total: <b>{totalElements}</b>
              }}
            />
          </span>
        </div>
      )}
      <ul className="dm-card-section__list">
        {cardsList.map((card, i) => i < countCards
          ? (
            <li key={card.srvId} className="dm-card-section__item">
              <DmResourceCard
                card={card}
                onClick={handleClickCard(card.srvId)}
              />
            </li>
          )
          : null
        )}
      </ul>
      <div className="dm-card-section__footer" ref={footerRef}>
        {showLoader && <Loader />}
      </div>
    </section>
  );
};
